import React, { useState, useEffect } from 'react';
import {
  FaUserTimes,
  FaSearch,
  FaFilter,
  FaUndo,
  FaCalendarAlt,
  FaExclamationTriangle,
} from 'react-icons/fa';
import api from '../../services/api';

const BlockedUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ text: '', type: '', show: false });

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const showMessage = (text, type) => {
    setMessage({ text, type, show: true });
    setTimeout(() => setMessage({ text: '', type: '', show: false }), 3000);
  };

  const fetchBlockedUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/blocked-users');
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch blocked users', error);
      showMessage('Failed to load blocked users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId) => {
    try {
      await api.post('/admin/blocked-users/unblock', {
        userId,
        action: 'unblock',
      });
      showMessage('User unblocked successfully', 'success');
      fetchBlockedUsers();
    } catch (error) {
      showMessage('Failed to unblock user', 'error');
    }
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.mobile?.includes(searchTerm)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-500 mb-4"></div>
          <p className="text-gray-600 text-lg">Loading blocked users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Message Popup */}
      {message.show && (
        <div
          className={`fixed top-4 right-4 z-50 transform transition-all duration-300 ${
            message.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white px-6 py-3 rounded-xl shadow-2xl flex items-center space-x-3 animate-slide-in`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              message.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            }`}
          >
            {message.type === 'success' ? '✓' : '✗'}
          </div>
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <FaUserTimes className="mr-3 text-red-500" />
              Blocked Accounts
            </h1>
            <p className="text-gray-600 mt-1">
              Manage users who have been blocked (manually or automatically via reports)
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchBlockedUsers}
              className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200 flex items-center"
            >
              <FaFilter className="mr-2" /> Refresh
            </button>
            <div className="px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium">
              {users.length} Blocked
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mt-6">
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>
      </div>

      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Blocked Users</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{users.length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <FaUserTimes className="text-red-500 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Auto-blocked (10+ Reports)</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {users.filter(u => {
                  // Optional: if you store block reason in future
                  return true;
                }).length}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
              <FaExclamationTriangle className="text-orange-500 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Manually Blocked</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {users.length} {/* You can differentiate later if needed */}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
              <FaUserTimes className="text-gray-500 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Blocked Users Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">
                  User Details
                </th>
                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">
                  Blocked On
                </th>
                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr
                    key={user._id}
                    className="hover:bg-gray-50/80 transition-colors duration-150"
                  >
                    <td className="px-8 py-5">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-white font-bold mr-3">
                          {user.name?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{user.name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">User ID: {user._id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-800">{user.email}</p>
                        <p className="text-sm text-gray-600">{user.mobile || 'No mobile'}</p>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center text-sm text-gray-600">
                        <FaCalendarAlt className="mr-2 text-gray-400" />
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <button
                        onClick={() => handleUnblock(user._id)}
                        className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-xl font-medium transition-colors duration-200 flex items-center"
                      >
                        <FaUndo className="mr-2" />
                        Unblock
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-16">
                    <div className="flex flex-col items-center">
                      <FaUserTimes className="text-6xl text-gray-300 mb-4" />
                      <p className="text-gray-600 text-lg font-medium">No blocked users</p>
                      <p className="text-gray-500 mt-1">
                        {searchTerm ? 'No matching results' : 'All users are active'}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-gray-600">
              Showing <span className="font-semibold">{filteredUsers.length}</span> of{' '}
              <span className="font-semibold">{users.length}</span> blocked users
            </p>
            <span className="text-sm text-gray-500">
              Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockedUsers;